import { Box } from '@zoopla-eng/zds-components/box';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { Gallery } from './zds/gallery';
import { RefreshMedium } from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { PlaceholderImage } from '@zoopla/assets';
import { Image } from '@zoopla/image';
import ListingCard, {
    getAmenities,
    getBadges,
    getBranchContactUrl,
    getDetailsLink,
    getListingImages,
    getListingType,
    getPremiumHighlights,
    getStatus
} from '@zoopla/listing-card';
import {
    AnalyticsEcommerce,
    AnalyticsTaxonomy,
    Favourites,
    SearchCategory,
    SearchContextType,
    SearchSource
} from '@zoopla/listing-search-context';
import { FiltersTrackingObj } from '@zoopla/listing-search-utils';
import { NewHomesListings } from '@zoopla/listing-search/new-homes-listings';
import {
    segmentSearchEvent,
    segmentSearchResultClickEvent,
    segmentSearchResultImpressionEvent
} from '@zoopla/listing-search/segment';
import Head from 'next/head';
import * as React from 'react';
import { FC, MouseEvent, useEffect, useState } from 'react';
import AdSlotDevicePair from './AdSlotDevicePair';
import { ErrorArea } from './ErrorArea';
import {
    detailsPageLinkStyle,
    galleryLinkStyle,
    galleryWrapper,
    layoutContentOverlayStyle,
    layoutContentStyle
} from './index.styles.css';
import { isBranchTaylorWimpey } from './isBranchTaylorWimpey';
import {
    listingRowStyle,
    listingsContainerStyle,
    mediaImageStyle
} from './Listings.styles.css';
import {
    agentContactTracking,
    agentLogoTracking,
    agentPhoneTracking,
    ecommerceClickTracking,
    getEcommerceDataForListing,
    imageGalleryControlTracking,
    imageTracking,
    listingContentTracking,
    phoneNumberRevealTracking
} from './Listings.tracking';
import { Listing as ListingType } from './Listings.types';
import { bodyStyle, cardStyle, titleStyle } from './NoResultsBanner.css';

type TrackingHandler = (props: {
    price?: string | null;
    listingId?: string | null;
}) => void;

type Props = {
    searchType: SearchContextType['searchType'];
    analyticsTaxonomyIdentifier: AnalyticsTaxonomy['searchIdentifier'];
    analyticsTaxonomy?: AnalyticsTaxonomy;
    searchCategory: SearchCategory;
    filters: FiltersTrackingObj;
    currentSearchSource: SearchSource;
    error?: { message: string };
    regularListings: ListingType[];
    extendedListings: ListingType[];
    onResetFilters: (evt: MouseEvent<HTMLButtonElement>) => void;
    favouritesListings: Favourites['listings'];
    authenticateSaveToggle: (
        listingId: string,
        onSuccess: ({ isSaved }: { isSaved: boolean }) => void
    ) => Promise<boolean>;

    authModal: React.ReactElement;
    analyticsEcommerce?: AnalyticsEcommerce;
    pageNumber: number;
    route: string;
};

const onCardClickCommon = (
    listingId: string,
    searchCategory: SearchCategory,
    filters: FiltersTrackingObj,
    currentSearchSource: SearchSource,
    price: string,
    pageNumber: number,
    analyticsEcommerce?: AnalyticsEcommerce,
    analyticsTaxonomy?: AnalyticsTaxonomy
) => {
    const ecommerceData =
        analyticsEcommerce &&
        getEcommerceDataForListing(analyticsEcommerce, listingId);

    if (ecommerceData) {
        ecommerceClickTracking(ecommerceData);
    }

    if (analyticsTaxonomy) {
        segmentSearchResultClickEvent(
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            listingId,
            price,
            pageNumber
        );
    }
};

export const Listings: FC<React.PropsWithChildren<Props>> = ({
    searchType,
    analyticsTaxonomyIdentifier,
    analyticsTaxonomy,
    analyticsEcommerce,
    searchCategory,
    filters,
    currentSearchSource,
    error,
    regularListings,
    extendedListings,
    onResetFilters,
    favouritesListings,
    authenticateSaveToggle,
    authModal,
    pageNumber
}) => {
    const [renderGalleryIdx, setRenderGalleryIdx] = useState<number>(-1);
    const [renderExtendedGalleryIdx, setRenderExtendedGalleryIdx] =
        useState<number>(-1);

    useEffect(() => {
        // on client render make sure we render in the gallery for the first
        // listing. Most of the time this is triggered by ListingCard.InView
        // but if there are featured properties at the top of the page the
        // counter will not increment until we have the first listing in view
        setRenderGalleryIdx((prev) => Math.max(prev, 0));
    }, []);

    if (error && error?.message === 'AREA_NOT_FOUND') {
        return (
            <ErrorArea
                areaName={analyticsTaxonomy?.areaName}
                searchType={searchType}
            />
        );
    }

    if (error) {
        return (
            <div className={cardStyle}>
                <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                    No results found
                </h2>
                <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                    Please amend your search options or try searching again.
                </p>
            </div>
        );
    }

    const onContactClick: TrackingHandler = ({ price, listingId }) => {
        segmentSearchEvent({
            eventName: 'Click Email Agent',
            analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            price,
            listingId,
            pageNumber
        });
        agentContactTracking();
    };

    const onPhoneClick: TrackingHandler = ({ price, listingId }) => {
        segmentSearchEvent({
            eventName: 'Click Call Agent',
            analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            price,
            listingId,
            pageNumber
        });
        agentPhoneTracking();
    };

    const onSaveClick: TrackingHandler = ({ price, listingId }) => {
        if (!listingId) {
            console.error('Listing ID undefined: Unable to save listing');
            return;
        }

        const onSuccess = ({ isSaved }: { isSaved: boolean }): void => {
            if (isSaved) {
                segmentSearchEvent({
                    eventName: 'Click Save Listing',
                    analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
                    currentSearchSource,
                    searchCategory,
                    filters,
                    price,
                    listingId,
                    pageNumber
                });

                segmentSearchEvent({
                    eventName: 'Save Listing',
                    analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
                    currentSearchSource,
                    searchCategory,
                    filters,
                    price,
                    listingId,
                    pageNumber
                });
            } else if (!isSaved) {
                segmentSearchEvent({
                    eventName: 'Unsave Listing',
                    analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
                    currentSearchSource,
                    searchCategory,
                    filters,
                    price,
                    listingId,
                    pageNumber
                });
            }
        };

        authenticateSaveToggle(listingId, onSuccess);
    };

    const onPhoneRevealClick: TrackingHandler = ({ price, listingId }) => {
        segmentSearchEvent({
            eventName: 'Phone Click To Reveal',
            analyticsTaxonomy: analyticsTaxonomy as AnalyticsTaxonomy,
            currentSearchSource,
            searchCategory,
            filters,
            price,
            listingId,
            pageNumber
        });
        phoneNumberRevealTracking();
    };

    const priorityImgIdx = 3;

    const [preloadImg] = getListingImages(regularListings[0], 1);

    return (
        <>
            {authModal}
            {Boolean(regularListings[0]?.image?.src) && (
                <Head>
                    <link
                        rel="preload"
                        as="image"
                        href=""
                        imageSrcSet={preloadImg.sources?.[0].srcSet} // use webp srcset
                        imageSizes={preloadImg.sizes}
                    />
                </Head>
            )}
            {regularListings.length === 0 && extendedListings.length === 0 ? (
                <div className={cardStyle}>
                    <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                        No results found
                    </h2>
                    <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                        Please amend your search options or try searching again.
                    </p>
                    <ButtonLeft
                        category="primary"
                        Icon={RefreshMedium}
                        onClick={onResetFilters}
                    >
                        Reset filters
                    </ButtonLeft>
                </div>
            ) : (
                ''
            )}
            <div
                className={listingsContainerStyle}
                data-testid="regular-listings"
            >
                {regularListings.map((listing, index) => {
                    const branchId = listing.branch?.branchId
                        ? Number(listing.branch?.branchId)
                        : null;
                    const imgLimit = index > renderGalleryIdx ? 1 : null;
                    const listingImages = getListingImages(listing, imgLimit);

                    const onClick = () => {
                        onCardClickCommon(
                            listing.listingId,
                            searchCategory,
                            filters,
                            currentSearchSource,
                            listing.price ?? '',
                            pageNumber,
                            analyticsEcommerce,
                            analyticsTaxonomy
                        );
                    };

                    const onMediaClick = () => {
                        onClick();
                        imageTracking();
                    };

                    const onContentClick = () => {
                        onClick();
                        listingContentTracking();
                    };

                    return (
                        <div
                            key={`listing_${listing.listingId}`}
                            id={`listing_${listing.listingId}`}
                            className={listingRowStyle}
                        >
                            <ListingCard.InView
                                onInView={() => {
                                    // render one ahead of what's in view
                                    setRenderGalleryIdx((prev) =>
                                        Math.max(prev, index + 1)
                                    );

                                    if (!analyticsTaxonomy) return;

                                    segmentSearchResultImpressionEvent(
                                        analyticsTaxonomy,
                                        currentSearchSource,
                                        searchCategory,
                                        filters,
                                        listing.listingId,
                                        listing.price ?? '',
                                        pageNumber
                                    );
                                }}
                            >
                                <ListingCard.Layout
                                    type={getListingType(listing)}
                                >
                                    <ListingCard.LayoutMedia>
                                        <a
                                            className={galleryLinkStyle({
                                                premium:
                                                    getListingType(listing) ===
                                                    'premium'
                                            })}
                                            href={getDetailsLink(
                                                analyticsTaxonomyIdentifier,
                                                listing.listingUris
                                            )}
                                        >
                                            {listingImages.length ? (
                                                index <= renderGalleryIdx ? (
                                                    <div
                                                        className={
                                                            galleryWrapper
                                                        }
                                                    >
                                                        <Gallery
                                                            images={
                                                                listingImages
                                                            }
                                                            type={getListingType(
                                                                listing
                                                            )}
                                                            objectFit="cover"
                                                            paginationAlignment="right"
                                                            numberOfFloorplans={
                                                                listing.numberOfFloorPlans ||
                                                                undefined
                                                            }
                                                            numberOfVideos={
                                                                listing.numberOfVideos ||
                                                                undefined
                                                            }
                                                            createCaption={(
                                                                index,
                                                                total
                                                            ) =>
                                                                `Property photo ${
                                                                    index + 1
                                                                } of ${total}. ${
                                                                    listingImages[
                                                                        index
                                                                    ].caption
                                                                }`
                                                            }
                                                            onSlideNext={() =>
                                                                imageGalleryControlTracking(
                                                                    listing.listingId
                                                                )
                                                            }
                                                            onSlidePrevious={() =>
                                                                imageGalleryControlTracking(
                                                                    listing.listingId
                                                                )
                                                            }
                                                            onSlideClick={
                                                                onMediaClick
                                                            }
                                                            hideArrows
                                                            isLazy={true}
                                                        />
                                                    </div>
                                                ) : (
                                                    <Image
                                                        className={
                                                            mediaImageStyle
                                                        }
                                                        src={
                                                            listingImages[0].src
                                                        }
                                                        sizes={
                                                            listingImages[0]
                                                                .sizes
                                                        }
                                                        alt={
                                                            listingImages[0]
                                                                .caption
                                                        }
                                                        important={
                                                            index <
                                                            priorityImgIdx
                                                        }
                                                        lazy={
                                                            index >
                                                            priorityImgIdx
                                                        }
                                                        onClick={onMediaClick}
                                                        sources={
                                                            listingImages[0]
                                                                .sources
                                                        }
                                                    />
                                                )
                                            ) : (
                                                <img
                                                    className={mediaImageStyle}
                                                    src={PlaceholderImage}
                                                    alt="Property images coming soon"
                                                    loading="lazy"
                                                />
                                            )}
                                        </a>
                                    </ListingCard.LayoutMedia>
                                    <ListingCard.LayoutContent
                                        onClick={onContentClick}
                                    >
                                        <div className={layoutContentStyle}>
                                            <div
                                                className={
                                                    layoutContentOverlayStyle
                                                }
                                            >
                                                <ListingCard.SaveListing
                                                    isSaved={favouritesListings.includes(
                                                        listing.listingId
                                                    )}
                                                    onSaveClick={(e) => {
                                                        e.stopPropagation();
                                                        onSaveClick({
                                                            price:
                                                                listing.price ??
                                                                '',
                                                            listingId:
                                                                listing.listingId
                                                        });
                                                    }}
                                                />
                                            </div>
                                            <a
                                                className={detailsPageLinkStyle}
                                                href={getDetailsLink(
                                                    analyticsTaxonomyIdentifier,
                                                    listing.listingUris
                                                )}
                                            >
                                                <Box
                                                    pl="S400"
                                                    pr="S400"
                                                    height="100%"
                                                    pt="S400"
                                                >
                                                    <ListingCard.Status
                                                        status={getStatus(
                                                            listing
                                                        )}
                                                    />
                                                    <Box pb="S200">
                                                        <ListingCard.Price
                                                            reducedPriceDate={
                                                                listing
                                                                    .priceDrop
                                                                    ?.lastPriceChangeDate
                                                            }
                                                            reducedPricePercentage={
                                                                listing
                                                                    .priceDrop
                                                                    ?.percentageChangeLabel
                                                            }
                                                            priceTitle={
                                                                listing.priceTitle
                                                            }
                                                            price={
                                                                listing.price
                                                            }
                                                            priceAlternative={
                                                                listing.alternativeRentFrequencyLabel
                                                            }
                                                        />
                                                    </Box>
                                                    <Box pb="S400">
                                                        <ListingCard.Amenities
                                                            {...getAmenities(
                                                                listing
                                                            )}
                                                        />
                                                    </Box>
                                                    <ListingCard.Summary
                                                        description={
                                                            listing.title as string
                                                        }
                                                        address={
                                                            listing.address as string
                                                        }
                                                        summary={
                                                            listing.summaryDescription
                                                        }
                                                    />
                                                    <ListingCard.Badges
                                                        badges={getBadges(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.PremiumAttributes
                                                        attributes={getPremiumHighlights(
                                                            listing
                                                        )}
                                                        type={getListingType(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Dates
                                                        availableFrom={
                                                            listing.availableFrom
                                                        }
                                                        availableFromLabel={
                                                            listing.availableFromLabel
                                                        }
                                                        publishedOn={
                                                            listing.publishedOn
                                                        }
                                                        publishedOnLabel={
                                                            listing.publishedOnLabel
                                                        }
                                                    />
                                                </Box>
                                            </a>
                                        </div>
                                    </ListingCard.LayoutContent>
                                    <ListingCard.LayoutFooterLeft>
                                        <ListingCard.AgentLogo
                                            href={
                                                listing.branch?.branchDetailsUri
                                            }
                                            src={listing.branch?.logoUrl}
                                            onClick={agentLogoTracking}
                                            title={listing.branch?.name}
                                            lazy={index > priorityImgIdx}
                                        />
                                    </ListingCard.LayoutFooterLeft>
                                    <ListingCard.LayoutFooterRight>
                                        {branchId ? (
                                            <ListingCard.ContactPhone
                                                branchId={branchId}
                                                phoneNumber={
                                                    listing.branch?.phone
                                                }
                                                listingId={Number(
                                                    listing.listingId
                                                )}
                                                onPhoneNumberClick={() =>
                                                    onPhoneClick({
                                                        price: listing.price,
                                                        listingId:
                                                            listing.listingId
                                                    })
                                                }
                                                onRevealPhoneNumberClick={() =>
                                                    onPhoneRevealClick({
                                                        price: listing.price,
                                                        listingId:
                                                            listing.listingId
                                                    })
                                                }
                                            />
                                        ) : null}
                                        <ListingCard.ContactEmail
                                            emailUrl={getBranchContactUrl(
                                                listing
                                            )}
                                            onClick={() =>
                                                onContactClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                        />
                                    </ListingCard.LayoutFooterRight>
                                    {branchId &&
                                    isBranchTaylorWimpey(branchId) ? (
                                        <NewHomesListings
                                            parentListingId={listing.listingId}
                                            branchId={branchId}
                                            searchIdentifier={
                                                analyticsTaxonomyIdentifier
                                            }
                                            analyticsTaxonomy={
                                                analyticsTaxonomy
                                            }
                                            searchSource={currentSearchSource}
                                            searchCategory={searchCategory}
                                            filters={filters}
                                            pageNumber={pageNumber}
                                            developmentUrl={
                                                listing.branch?.branchDetailsUri
                                            }
                                        />
                                    ) : null}
                                </ListingCard.Layout>
                            </ListingCard.InView>
                            <AdSlotDevicePair index={index} />
                        </div>
                    );
                })}

                {regularListings.length === 0 && extendedListings.length > 0 ? (
                    <div className={cardStyle}>
                        <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                            No results found
                        </h2>
                        <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                            However, we did find some properties nearby by
                            increasing your search radius.
                        </p>
                    </div>
                ) : (
                    ''
                )}

                {regularListings.length > 0 && extendedListings.length > 0 ? (
                    <div className={cardStyle}>
                        <h2 className={`${titleStyle} ${fontGroups.mug}`}>
                            No more exact results
                        </h2>
                        <p className={`${bodyStyle} ${fontGroups.whisk}`}>
                            However, we did find some properties nearby by
                            increasing your search radius.
                        </p>
                    </div>
                ) : (
                    ''
                )}

                {extendedListings.map((listing, index) => {
                    const listingImages = getListingImages(listing);
                    const branchId = listing.branch?.branchId
                        ? Number(listing.branch?.branchId)
                        : null;

                    const onClick = () => {
                        onCardClickCommon(
                            listing.listingId,
                            searchCategory,
                            filters,
                            currentSearchSource,
                            listing.price ?? '',
                            pageNumber,
                            analyticsEcommerce,
                            analyticsTaxonomy
                        );
                    };

                    const onMediaClick = () => {
                        onClick();
                        imageTracking();
                    };

                    const onContentClick = () => {
                        onClick();
                        listingContentTracking();
                    };

                    return (
                        <div
                            key={`listing_${listing.listingId}`}
                            id={`listing_${listing.listingId}`}
                            className={listingRowStyle}
                        >
                            <ListingCard.InView
                                onInView={() => {
                                    setRenderExtendedGalleryIdx((prev) =>
                                        Math.max(prev, index)
                                    );
                                    if (!analyticsTaxonomy) return;

                                    segmentSearchResultImpressionEvent(
                                        analyticsTaxonomy,
                                        currentSearchSource,
                                        searchCategory,
                                        filters,
                                        listing.listingId,
                                        listing.price ?? '',
                                        pageNumber
                                    );
                                }}
                            >
                                <ListingCard.Layout
                                    type={getListingType(listing)}
                                >
                                    <ListingCard.LayoutMedia>
                                        <a
                                            className={galleryLinkStyle({
                                                premium:
                                                    getListingType(listing) ===
                                                    'premium'
                                            })}
                                            href={getDetailsLink(
                                                analyticsTaxonomyIdentifier,
                                                listing.listingUris
                                            )}
                                        >
                                            {listingImages.length ? (
                                                index <=
                                                renderExtendedGalleryIdx ? (
                                                    <div
                                                        className={
                                                            galleryWrapper
                                                        }
                                                    >
                                                        <Gallery
                                                            images={
                                                                listingImages
                                                            }
                                                            type={getListingType(
                                                                listing
                                                            )}
                                                            objectFit="cover"
                                                            paginationAlignment="right"
                                                            numberOfFloorplans={
                                                                listing.numberOfFloorPlans ||
                                                                undefined
                                                            }
                                                            numberOfVideos={
                                                                listing.numberOfVideos ||
                                                                undefined
                                                            }
                                                            createCaption={(
                                                                index,
                                                                total
                                                            ) =>
                                                                `Property photo ${
                                                                    index + 1
                                                                } of ${total}. ${
                                                                    listingImages[
                                                                        index
                                                                    ].caption
                                                                }`
                                                            }
                                                            onSlideNext={() =>
                                                                imageGalleryControlTracking(
                                                                    listing.listingId
                                                                )
                                                            }
                                                            onSlidePrevious={() =>
                                                                imageGalleryControlTracking(
                                                                    listing.listingId
                                                                )
                                                            }
                                                            onSlideClick={
                                                                onMediaClick
                                                            }
                                                            hideArrows
                                                            isLazy={true}
                                                        />
                                                    </div>
                                                ) : (
                                                    <Image
                                                        className={
                                                            mediaImageStyle
                                                        }
                                                        src={
                                                            listingImages[0].src
                                                        }
                                                        sizes={
                                                            listingImages[0]
                                                                .sizes
                                                        }
                                                        alt={
                                                            listingImages[0]
                                                                .caption
                                                        }
                                                        important={
                                                            index <
                                                            priorityImgIdx
                                                        }
                                                        lazy={
                                                            index >
                                                            priorityImgIdx
                                                        }
                                                        onClick={onMediaClick}
                                                        sources={
                                                            listingImages[0]
                                                                .sources
                                                        }
                                                    />
                                                )
                                            ) : (
                                                <img
                                                    className={mediaImageStyle}
                                                    src={PlaceholderImage}
                                                    alt="Property images coming soon"
                                                    loading="lazy"
                                                />
                                            )}
                                        </a>
                                    </ListingCard.LayoutMedia>
                                    <ListingCard.LayoutContent
                                        onClick={onContentClick}
                                    >
                                        <div className={layoutContentStyle}>
                                            <div
                                                className={
                                                    layoutContentOverlayStyle
                                                }
                                            >
                                                <ListingCard.SaveListing
                                                    isSaved={favouritesListings.includes(
                                                        listing.listingId
                                                    )}
                                                    onSaveClick={(e) => {
                                                        e.stopPropagation();
                                                        onSaveClick({
                                                            price:
                                                                listing.price ??
                                                                '',
                                                            listingId:
                                                                listing.listingId
                                                        });
                                                    }}
                                                />
                                            </div>
                                            <a
                                                className={detailsPageLinkStyle}
                                                href={getDetailsLink(
                                                    analyticsTaxonomyIdentifier,
                                                    listing.listingUris
                                                )}
                                            >
                                                <Box p="S400" height="100%">
                                                    <ListingCard.Status
                                                        status={getStatus(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Price
                                                        reducedPriceDate={
                                                            listing.priceDrop
                                                                ?.lastPriceChangeDate
                                                        }
                                                        reducedPricePercentage={
                                                            listing.priceDrop
                                                                ?.percentageChangeLabel
                                                        }
                                                        priceTitle={
                                                            listing.priceTitle
                                                        }
                                                        price={listing.price}
                                                        priceAlternative={
                                                            listing.alternativeRentFrequencyLabel
                                                        }
                                                    />
                                                    <ListingCard.Amenities
                                                        {...getAmenities(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Summary
                                                        description={
                                                            listing.title as string
                                                        }
                                                        address={
                                                            listing.address as string
                                                        }
                                                        summary={
                                                            listing.summaryDescription
                                                        }
                                                    />
                                                    <ListingCard.Badges
                                                        badges={getBadges(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.PremiumAttributes
                                                        attributes={getPremiumHighlights(
                                                            listing
                                                        )}
                                                        type={getListingType(
                                                            listing
                                                        )}
                                                    />
                                                    <ListingCard.Dates
                                                        availableFrom={
                                                            listing.availableFrom
                                                        }
                                                        availableFromLabel={
                                                            listing.availableFromLabel
                                                        }
                                                        publishedOn={
                                                            listing.publishedOn
                                                        }
                                                        publishedOnLabel={
                                                            listing.publishedOnLabel
                                                        }
                                                    />
                                                </Box>
                                            </a>
                                        </div>
                                    </ListingCard.LayoutContent>
                                    <ListingCard.LayoutFooterLeft>
                                        <ListingCard.AgentLogo
                                            href={
                                                listing.branch?.branchDetailsUri
                                            }
                                            src={listing.branch?.logoUrl}
                                            onClick={agentLogoTracking}
                                            title={listing.branch?.name}
                                            lazy={index > priorityImgIdx}
                                        />
                                    </ListingCard.LayoutFooterLeft>
                                    <ListingCard.LayoutFooterRight>
                                        {branchId ? (
                                            <ListingCard.ContactPhone
                                                branchId={branchId}
                                                phoneNumber={
                                                    listing.branch?.phone
                                                }
                                                listingId={Number(
                                                    listing.listingId
                                                )}
                                                onPhoneNumberClick={() =>
                                                    onPhoneClick({
                                                        price: listing.price,
                                                        listingId:
                                                            listing.listingId
                                                    })
                                                }
                                                onRevealPhoneNumberClick={() =>
                                                    onPhoneRevealClick({
                                                        price: listing.price,
                                                        listingId:
                                                            listing.listingId
                                                    })
                                                }
                                            />
                                        ) : null}
                                        <ListingCard.ContactEmail
                                            emailUrl={getBranchContactUrl(
                                                listing
                                            )}
                                            onClick={() =>
                                                onContactClick({
                                                    price: listing.price,
                                                    listingId: listing.listingId
                                                })
                                            }
                                        />
                                    </ListingCard.LayoutFooterRight>
                                </ListingCard.Layout>
                            </ListingCard.InView>
                            <AdSlotDevicePair index={index} />
                        </div>
                    );
                })}
            </div>
        </>
    );
};
